import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { injectIntl } from 'react-intl';
import { VictoryChart, VictoryBar, VictoryStack, VictoryAxis, VictoryTooltip } from 'victory';

import { bem } from 'helpers/class-name';
import { intlShape } from 'constants/propTypes';
import { ICONS } from 'constants/icons';
import { Card, CardTitle } from 'components/Card';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';

import SmartToolsContext from '../SmartToolsContext';
import DataComingSoon from './DataComingSoon';
import {
  getSmartSpotFillChartTheme,
  colorGridLine,
  colorDirectFill,
  colorSmartSpotFill,
  colorUnfilled,
} from './SmartSpotFillChartTheme';
import { messages } from './SmartSpotFillChart.messages';
import SmartSpotFillTooltip from './SmartSpotFillTooltip';
import './SmartSpotFillChart.scss';
import { SMARTSPOT_CATEGORY_TYPE } from './SmartTools.helpers';

const { block, element } = bem('smart-spot-fill-chart');

const BAR_RATIO = 0.7;

// Define the percentage buckets for the x-axis
const PERCENTAGE_BUCKETS = [
  { min: 0, max: 30, label: SMARTSPOT_CATEGORY_TYPE.ZERO_TO_THIRTY },
  { min: 30, max: 40, label: SMARTSPOT_CATEGORY_TYPE.THIRTY_TO_FOURTY },
  { min: 40, max: 50, label: SMARTSPOT_CATEGORY_TYPE.FOURTY_TO_FIFTY },
  { min: 50, max: 60, label: SMARTSPOT_CATEGORY_TYPE.FIFTY_TO_SIXTY },
  { min: 60, max: 70, label: SMARTSPOT_CATEGORY_TYPE.SIXTY_TO_SEVENTY },
  { min: 70, max: 80, label: SMARTSPOT_CATEGORY_TYPE.SEVENTY_TO_EIGHTY },
  { min: 80, max: 90, label: SMARTSPOT_CATEGORY_TYPE.EIGHTY_TO_NINETY },
  { min: 90, max: 100, label: SMARTSPOT_CATEGORY_TYPE.NINETY_TO_ONE_HUNDRED },
];

const processChartData = data => {
  return PERCENTAGE_BUCKETS.map(bucket => {
    const bucketData = data.find(d => {
      // 0-30% bucket: handle case where bucketMin of 0 is not returned by the API
      if (bucket.min === 0 && bucket.max === 30) {
        return !d.bucketMin && d.bucketMax === bucket.max;
      }
      return d.bucketMin === bucket.min && d.bucketMax === bucket.max;
    });

    if (
      !bucketData ||
      (bucketData.directUtilizationReservationCount === 0 && bucketData.cpUtilizationReservationCount === 0)
    ) {
      return {
        min: bucket.min,
        category: bucket.label,
        directFillPercentage: 0,
        smartspotFillPercentage: 0,
        unfilledPercentage: 0,
        noData: 1,
      };
    }

    const directFill = bucketData.directUtilizationReservationCount || 0;
    const smartspotFill = bucketData.cpUtilizationReservationCount || 0;
    const unfilledSpots = bucketData.unfilledReservationCount || 0;
    const totalCapacity = bucketData.totalCapacity || 0;

    const directFillPercentage = bucketData.directUtilizationPercentage || 0;
    const smartspotFillPercentage = bucketData.cpUtilizationPercentage || 0;
    const unfilledPercentage = bucketData.unfilledPercentage || 0;

    return {
      category: bucket.label,
      directFill,
      smartspotFill,
      unfilledSpots,
      totalCapacity,
      directFillPercentage,
      smartspotFillPercentage,
      unfilledPercentage,
      noData: 0,
    };
  });
};

const SmartSpotFillChart = ({ intl, isChartFeatureEnabled }) => {
  const { locale } = intl;
  const chartTheme = getSmartSpotFillChartTheme(locale);

  const { chartData: venueSmartspotPerformanceChartData, isLoading: isChartDataLoading } =
    useContext(SmartToolsContext);

  const showDataComingSoon =
    !isChartFeatureEnabled || (!isChartDataLoading && venueSmartspotPerformanceChartData.length === 0);

  const processedData = processChartData(venueSmartspotPerformanceChartData);

  const CHART_HEIGHT_IN_PX = 140;

  const chartProps = {
    height: CHART_HEIGHT_IN_PX,
    padding: { top: 20, right: 20, left: 20, bottom: 10 },
    domainPadding: { x: 5, y: 0 },
    domain: { y: [0, 100] },
    theme: chartTheme,
  };

  const filteredProcessedData = processedData.filter(d => !d.noData);

  const renderChartContent = () => {
    if (isChartDataLoading && !showDataComingSoon) {
      return <LoadingDots />;
    }

    if (showDataComingSoon) {
      return <DataComingSoon />;
    }

    return (
      <>
        <div className={element('timeframe')}>
          <Message message={messages.timeframe} />
        </div>

        <VictoryChart {...chartProps}>
          <VictoryAxis
            tickValues={PERCENTAGE_BUCKETS.map(b => b.label)}
            tickFormat={t => t}
            style={{
              tickLabels: { angle: 0, fontWeight: 400, fontSize: 10, padding: 5, fill: '#000000' },
              grid: { stroke: 'none' },
            }}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={() => ''} // Hide y-axis labels
            style={{
              grid: { stroke: colorGridLine, strokeWidth: 1 },
              tickLabels: { fill: 'transparent' },
            }}
          />

          {/* Render striped bars for no-data buckets (full height) */}
          <VictoryStack>
            <VictoryBar
              barRatio={BAR_RATIO}
              data={processedData}
              x="category"
              y={d => (d.noData ? 100 : 0)}
              style={{
                data: {
                  fill: 'url(#smartSpotFillChartDiagonalStripes)',
                  strokeWidth: 0,
                },
              }}
            />

            <VictoryBar
              barRatio={BAR_RATIO}
              data={filteredProcessedData}
              x="category"
              y="directFillPercentage"
              labels={() => ''}
              labelComponent={<VictoryTooltip flyoutComponent={<SmartSpotFillTooltip />} />}
              style={{
                data: {
                  fill: colorDirectFill,
                  strokeWidth: 0,
                },
              }}
            />
            <VictoryBar
              barRatio={BAR_RATIO}
              data={filteredProcessedData}
              x="category"
              y="smartspotFillPercentage"
              labels={() => ''}
              labelComponent={<VictoryTooltip flyoutComponent={<SmartSpotFillTooltip />} />}
              style={{
                data: {
                  fill: colorSmartSpotFill,
                  strokeWidth: 0,
                },
              }}
            />
            <VictoryBar
              barRatio={BAR_RATIO}
              data={filteredProcessedData}
              x="category"
              y="unfilledPercentage"
              labels={() => ''}
              labelComponent={<VictoryTooltip flyoutComponent={<SmartSpotFillTooltip />} />}
              style={{
                data: {
                  fill: colorUnfilled,
                  strokeWidth: 0,
                },
              }}
            />
          </VictoryStack>
        </VictoryChart>
      </>
    );
  };

  return (
    <Card className={block()}>
      <div className={element('header')}>
        <div className={element('title-section')}>
          <Message as={CardTitle} className={element('title')} message={messages.title} />
          <p className={element('subtitle')}>
            <Message message={messages.subtitle} />
          </p>
        </div>
      </div>

      {renderChartContent()}

      <div className={element('footer')}>
        <div className={element('x-axis-label')}>
          <Message message={messages.xAxisLabel} />
          <TooltipToggle name="smart-spot-fill-chart-info" className={element('info-icon-wrapper')}>
            <Isvg src={ICONS.info} className={element('info-icon')} />
          </TooltipToggle>
          <TooltipContent name="smart-spot-fill-chart-info" place="top">
            <Message message={messages.infoTooltip} />
          </TooltipContent>
        </div>
        <div className={element('legend')}>
          <div className={element('legend-item')}>
            <span className={element('legend-color', { direct: true })} />
            <Message message={messages.legendDirectFill} />
          </div>
          <div className={element('legend-item')}>
            <span className={element('legend-color', { smartspot: true })} />
            <Message message={messages.legendSmartSpotFill} />
          </div>
          <div className={element('legend-item')}>
            <Isvg className={element('legend-color', { 'no-data': true })} src={ICONS.noDataLegend} />
            <Message message={messages.legendNoData} />
          </div>
        </div>
      </div>

      {/* SVG pattern definition for striped bars - wrapped to avoid Card margin-top */}
      <span className={element('pattern-wrapper')}>
        <svg width="0" height="0">
          <defs>
            <pattern
              id="smartSpotFillChartDiagonalStripes"
              patternUnits="userSpaceOnUse"
              width="3"
              height="3"
              patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke="#E0E0E0" strokeWidth="3" />
            </pattern>
          </defs>
        </svg>
      </span>
    </Card>
  );
};

SmartSpotFillChart.propTypes = {
  isChartFeatureEnabled: PropTypes.bool,
  intl: intlShape.isRequired,
};

SmartSpotFillChart.defaultProps = {
  isChartFeatureEnabled: false,
};

export default injectIntl(SmartSpotFillChart);
